export type LongPressDetail = {
  clientX: number;
  clientY: number;
};

export type LongPressOptions = {
  /** Hold duration before firing (default 480ms). */
  ms?: number;
  /** Cancel if pointer moves farther than this (default 12px). */
  moveTolerance?: number;
};

/**
 * Long-press on touch-first devices. Does not use native text selection.
 * Returns detach cleanup.
 */
export function attachLongPress(
  el: HTMLElement,
  onLongPress: (detail: LongPressDetail) => void,
  options: LongPressOptions = {}
): () => void {
  const ms = options.ms ?? 480;
  const moveTolerance = options.moveTolerance ?? 12;

  let timer: ReturnType<typeof setTimeout> | null = null;
  let startX = 0;
  let startY = 0;
  let pointerId = -1;
  let suppressClick = false;

  const clearTimer = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0 && e.pointerType !== 'touch') return;
    clearTimer();
    suppressClick = false;
    pointerId = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    timer = setTimeout(() => {
      timer = null;
      suppressClick = true;
      onLongPress({ clientX: startX, clientY: startY });
      window.setTimeout(() => {
        suppressClick = false;
      }, 400);
    }, ms);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (e.pointerId !== pointerId || !timer) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (dx * dx + dy * dy > moveTolerance * moveTolerance) clearTimer();
  };

  const finish = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    clearTimer();
    pointerId = -1;
  };

  const onContextMenu = (e: Event) => {
    if (suppressClick) e.preventDefault();
  };

  const onClickCapture = (e: Event) => {
    if (suppressClick) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  el.addEventListener('pointerdown', onPointerDown);
  el.addEventListener('pointermove', onPointerMove);
  el.addEventListener('pointerup', finish);
  el.addEventListener('pointercancel', finish);
  el.addEventListener('contextmenu', onContextMenu);
  el.addEventListener('click', onClickCapture, true);

  return () => {
    clearTimer();
    el.removeEventListener('pointerdown', onPointerDown);
    el.removeEventListener('pointermove', onPointerMove);
    el.removeEventListener('pointerup', finish);
    el.removeEventListener('pointercancel', finish);
    el.removeEventListener('contextmenu', onContextMenu);
    el.removeEventListener('click', onClickCapture, true);
  };
}
